<template>
  <div class="flex h-screen w-full">
    <!-- Mobile Overlay -->
    <div 
      v-if="isMobileMenuOpen"
      @click="closeMobileMenu"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
    ></div>

    <!-- Sidebar -->
    <div 
      :class="[
        'fixed lg:static inset-y-0 left-0 z-50 lg:z-auto',
        'w-64 bg-sidebar border-r border-sidebar-border',
        'transform transition-transform duration-300 ease-in-out',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ]"
    >
      <div class="p-4 border-b border-sidebar-border">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Clock class="h-6 w-6 text-primary" />
            <span class="font-semibold text-sidebar-foreground">Time-Manager</span>
          </div>
          <button 
            @click="closeMobileMenu"
            class="lg:hidden p-1 rounded-md hover:bg-sidebar-accent"
          >
            <X class="h-5 w-5 text-sidebar-foreground" />
          </button>
        </div>
      </div>
      
      <div class="p-4">
        <div class="space-y-1">
          <p class="text-xs font-medium text-sidebar-foreground/70 mb-2">Navigation</p>
          <div 
            v-for="item in navigationItems" 
            :key="item.id"
            @click="handleNavigationClick(item.id)"
            :class="[
              'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-all duration-200 touch-target',
              'hover:scale-[1.02] active:scale-[0.98] focus-enhanced',
              currentView === item.id 
                ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-sm animate-scale-in' 
                : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            ]"
            :aria-current="currentView === item.id ? 'page' : undefined"
            :aria-label="item.label"
          >
            <component :is="item.icon" class="h-4 w-4 flex-shrink-0" />
            <span class="font-medium">{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col lg:ml-0">
      <!-- Top Navigation Bar -->
      <header class="border-b bg-background px-4 lg:px-6 py-3 sticky top-0 z-30">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button 
              @click="openMobileMenu"
              class="lg:hidden touch-target rounded-md hover:bg-muted focus-enhanced transition-colors"
              aria-label="Open navigation menu"
            >
              <Menu class="h-5 w-5" />
            </button>
            <div class="flex flex-col gap-1">
              <h1 class="text-lg font-semibold capitalize">
                {{ getPageTitle(currentView) }}
              </h1>
              <Breadcrumb :current-view="currentView" :current-tab="currentTab" />
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <NotificationCenter />
            <UserProfileDropdown @view-change="handleDropdownNavigation" />
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 overflow-auto bg-gray-50">
        <div class="min-h-full">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { inject, computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import UserProfileDropdown from './UserProfileDropdown.vue'
import Breadcrumb from './Breadcrumb.vue'
import NotificationCenter from './NotificationCenter.vue'
import { Clock, Home, Users, BarChart, DollarSign, Calendar, Settings, Menu, X, CheckSquare, ClipboardList, UserCheck } from 'lucide-vue-next'

export default {
  name: 'Layout',
  components: {
    UserProfileDropdown,
    Breadcrumb,
    NotificationCenter,
    Clock,
    Home,
    Users,
    BarChart,
    DollarSign,
    Calendar,
    Settings,
    Menu,
    X,
    CheckSquare,
    ClipboardList,
    UserCheck
  },
  props: {
    currentView: {
      type: String,
      required: true
    },
    currentTab: {
      type: String,
      default: null
    }
  },
  emits: ['view-change'],
  setup(props, { emit }) {
    const auth = inject('auth')
    const router = useRouter()
    const route = useRoute()
    const isMobileMenuOpen = ref(false)

    const navigationItems = computed(() => {
      const baseItems = [
        { id: 'dashboard', label: 'Dashboard', icon: Home }
      ]

      // Add tasks navigation only for employees (not managers or admins)
      if (auth.user.value && auth.user.value.role === 'employee') {
        baseItems.push({ id: 'tasks', label: 'My Tasks', icon: ClipboardList })
      }

      // Manager-specific navigation
      if (auth.user.value && auth.user.value.role === 'manager') {
        baseItems.push({ id: 'manager-tasks', label: 'Task Management', icon: UserCheck })
        baseItems.push({ id: 'teams', label: 'Teams', icon: Users })
        baseItems.push({ id: 'schedule', label: 'Schedule', icon: Calendar })
        baseItems.push({ id: 'create-user', label: 'Create User', icon: Users })
      }

      // Admin-specific navigation
      if (auth.user.value && auth.user.value.role === 'admin') {
        baseItems.push({ id: 'admin-tasks', label: 'Task Administration', icon: Settings })
        baseItems.push({ id: 'organization', label: 'Organization', icon: Users })
        baseItems.push({ id: 'teams', label: 'Teams', icon: Users })
        baseItems.push({ id: 'schedule', label: 'Schedule', icon: Calendar })
        baseItems.push({ id: 'payroll', label: 'Payroll', icon: DollarSign })
        baseItems.push({ id: 'create-user', label: 'Create User', icon: Users })
      }

      return baseItems
    })

    const handleNavigationClick = (viewId) => {
      if (auth.user.value) {
        closeMobileMenu()
        
        nextTick(() => {
          switch (viewId) {
          case 'dashboard':
            // All users go to /dashboard - AppContent handles role-based component rendering
            router.push('/dashboard')
            break
          case 'tasks':
            router.push('/tasks')
            break
          case 'manager-tasks':
            router.push('/manager-tasks')
            break
          case 'admin-tasks':
            router.push('/admin-tasks')
            break
          case 'organization':
            router.push('/organization')
            break
          case 'teams':
            router.push('/team-board')
            break
          case 'schedule':
            router.push('/schedule')
            break
          case 'payroll':
            router.push('/payroll')
            break
          case 'create-user':
            router.push('/create-user')
            break
            case 'profile':
              router.push('/profile-settings')
              break
          }
        })
      }
    }

    const handleDropdownNavigation = (viewId) => {
      handleNavigationClick(viewId)
    }

    // Mobile menu functions
    const openMobileMenu = () => {
      isMobileMenuOpen.value = true
    }

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false
    }

    // Improved page title logic
    const getPageTitle = (view) => {
      const titles = {
        dashboard: 'Dashboard',
        charts: 'Analytics',
        tasks: 'My Tasks',
        'manager-tasks': 'Task Management',
        'admin-tasks': 'Task Administration',
        organization: 'Organization Management',
        teams: 'Team Management',
        schedule: 'Schedule',
        payroll: 'Payroll',
        'create-user': 'Create User',
        profile: 'Profile Settings'
      }
      return titles[view] || view.charAt(0).toUpperCase() + view.slice(1)
    }

    // Close mobile menu on escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen.value) {
        closeMobileMenu()
      }
    }

    onMounted(() => {
      // Add event listeners
      document.addEventListener('keydown', handleEscapeKey)
    })

    onUnmounted(() => {
      // Clean up event listeners
      document.removeEventListener('keydown', handleEscapeKey)
    })

    return {
      navigationItems,
      isMobileMenuOpen,
      handleNavigationClick,
      handleDropdownNavigation,
      openMobileMenu,
      closeMobileMenu,
      getPageTitle
    }
  }
}
</script>